import { Link } from 'react-router-dom';
import { BookOpen, Download, ExternalLink, ArrowLeft, Sun, Moon, CheckCircle } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';

export default function StudyGuide() {
    const { t } = useTranslation();
    const { theme, toggleTheme } = useTheme();

    const chapters = t('study_guide.chapters', { returnObjects: true }) as string[];

    const schema = {
        "@context": "https://schema.org",
        "@type": "Course",
        "name": t('study_guide.title') || "Guide d'étude",
        "description": t('study_guide.subtitle') || "Guide de préparation à l'examen",
        "provider": {
            "@type": "Organization",
            "name": "CitoyenPro",
            "sameAs": "https://citoyenpro.nnt-quebec.com"
        }
    };

    return (
        <div className="min-h-screen bg-dark-bg text-text-main font-sans">
            <SEO
                title={t('study_guide.title')}
                description={t('study_guide.subtitle')}
                keywords="guide etude citoyennete canadienne, livre decouvrir le canada, chapitres examen canada"
                canonical="/study-guide"
                schema={schema}
            />
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-dark-bg/80 backdrop-blur-md border-b border-dark-border">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <div className="w-10 h-10 shrink-0">
                            <img src="/logo/app-logo.png" alt="Logo" className="w-full h-full object-contain" />
                        </div>
                        <span className="text-2xl font-bold text-text-main">CitoyenPro</span>
                    </Link>
                    <div className="flex items-center gap-4">
                        <button
                            onClick={toggleTheme}
                            className="p-2 rounded-xl text-text-muted hover:bg-dark-hover hover:text-text-main transition-colors"
                        >
                            {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                        </button>
                        <Link to="/signup" className="px-5 py-2.5 bg-primary text-white rounded-full font-bold hover:bg-primary-hover transition-all text-sm">
                            {t('study_guide.train_now')}
                        </Link>
                    </div>
                </div>
            </nav>

            <main className="pt-32 pb-24 px-6">
                <div className="max-w-4xl mx-auto">
                    <Link to="/" className="inline-flex items-center gap-2 text-text-muted hover:text-primary transition-colors mb-8">
                        <ArrowLeft size={20} />
                        {t('study_guide.back_home')}
                    </Link>

                    <div className="text-center mb-16">
                        <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-primary/10 text-primary mb-6">
                            <BookOpen size={40} />
                        </div>
                        <h1 className="text-4xl md:text-5xl font-bold text-text-main mb-6">
                            {t('study_guide.title')}
                        </h1>
                        <p className="text-xl text-text-muted max-w-2xl mx-auto">
                            {t('study_guide.subtitle')}
                        </p>
                    </div>


                    {/* Chapters List */}
                    <div className="mb-16">
                        <h3 className="text-2xl font-bold text-text-main mb-8 text-center">
                            {t('study_guide.learn_title')}
                        </h3>
                        <div className="grid md:grid-cols-2 gap-4">
                            {Array.isArray(chapters) && chapters.map((chapter, index) => (
                                <div key={index} className="flex items-start gap-4 p-4 bg-dark-card border border-dark-border rounded-xl hover:border-primary/30 transition-colors">
                                    <div className="shrink-0 mt-0.5 text-primary">
                                        <CheckCircle size={20} />
                                    </div>
                                    <span className="text-text-main font-medium">{chapter}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* CTA */}
                    <div className="text-center bg-gradient-to-br from-primary/10 to-orange-500/10 rounded-3xl p-12 border border-primary/20">
                        <h2 className="text-3xl font-bold text-text-main mb-4">
                            {t('study_guide.cta.title')}
                        </h2>
                        <p className="text-text-muted mb-8 max-w-xl mx-auto">
                            {t('study_guide.cta.desc')}
                        </p>
                        <Link to="/signup" className="inline-flex px-8 py-4 bg-primary text-white text-lg font-bold rounded-full hover:bg-primary-hover transition-all shadow-lg hover:shadow-primary/25 hover:scale-105">
                            {t('study_guide.cta.button')}
                        </Link>
                    </div>
                </div>
            </main>

            {/* Footer Compact */}
            <footer className="bg-dark-card border-t border-dark-border py-8 text-center text-sm text-text-muted">
                <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-center items-center gap-6">
                    <p>{t('study_guide.footer.copyright')}</p>
                    <div className="flex gap-6">
                        <Link to="/legal/privacy" className="hover:text-primary transition-colors">{t('study_guide.footer.privacy')}</Link>
                        <Link to="/legal/terms" className="hover:text-primary transition-colors">{t('study_guide.footer.terms')}</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}
